import React, { useCallback, useMemo } from 'react';
import { MenuItem } from '../types';
import { useLanguage } from '../../../context/LanguageContext';
import { useGetDynamicReportingMenu } from '../../../hooks/PowerBI';
import { pageModule, useRights } from '../../../rights';
import { useBrandConfig } from '../../../context/BrandConfigContext';

// Retire les entrées dont la page est refusée (même table que la garde de route) et les groupes devenus vides,
// comme le menu BeOne qui masque un groupe quand aucune de ses entrées n'est visible
const filterByRights = (items: MenuItem[], canOpen: (path: string) => boolean): MenuItem[] =>
    items.flatMap(item => {
        if (item.path && !canOpen(item.path)) return [];
        if (!item.subMenu) return [item];
        const subMenu = filterByRights(item.subMenu, canOpen);
        return subMenu.length > 0 || item.isLoading ? [{ ...item, subMenu }] : [];
    });

export const useMenuStructure = (): { menuItems: MenuItem[]; loading: boolean } => {
    const { t } = useLanguage();
    const { reportingMenuItems, loading: powerBiLoading } = useGetDynamicReportingMenu();
    const { ready: rightsReady, failed: rightsFailed, canOpen: canOpenWithRights } = useRights();
    const { ready: brandReady, loading: brandLoading, setting } = useBrandConfig();
    // Entrée visible si les droits l'autorisent et, pour une page liée à un module d'enseigne, si l'enseigne l'a
    // (masquée tant que la configuration n'est pas chargée, pour ne pas l'afficher puis la retirer)
    const canOpen = useCallback((path: string) => {
        const module = pageModule(path);
        return canOpenWithRights(path) && (!module || (brandReady && setting(module)));
    }, [canOpenWithRights, brandReady, setting]);

    const menuStructure = useMemo(() => {
        const menu: MenuItem[] = [
            {
                id: 'dashboards',
                label: t('menu.dashboards'),
                icon: 'bx bx-home-circle',
                path: '/dashboard'
            },
            {
                id: 'marketing',
                label: t('menu.marketingAnimation'),
                icon: 'bx bx-target-lock',
                subMenu: [
                    {
                        id: 'marketing-calendar',
                        label: t('menu.calendar'),
                        path: '/marketing/calendar'
                    },
                    {
                        id: 'marketing-new',
                        label: t('menu.newAnimation'),
                        path: '/marketing/new-campaign'
                    },
                    {
                        id: 'marketing-preconizations',
                        label: t('menu.preconizations'),
                        path: '/marketing/preconizations'
                    },
                    {
                        id: 'marketing-search',
                        label: t('menu.advancedSearch'),
                        path: '/marketing/advanced-search'
                    },
                    {
                        id: 'marketing-drafts',
                        label: t('menu.drafts'),
                        path: '/marketing/drafts'
                    },
                    {
                        id: 'marketing-offers',
                        label: t('menu.recurringOffer'),
                        path: '/marketing/recurring-offer'
                    },
                    {
                        id: 'marketing-journey',
                        label: t('menu.customerJourney'),
                        path: '/marketing/customer-journey'
                    },
                    {
                        id: 'marketing-timeslots',
                        label: t('menu.timeslots'),
                        path: '/marketing/timeslots'
                    }
                ]
            },
            ...(reportingMenuItems ? [convertRouteTypeToMenuItem(reportingMenuItems)] : []),
            {
                id: 'customers',
                label: t('menu.myCustomers'),
                icon: 'bx bx-group',
                subMenu: [
                    {
                        id: 'customers-search',
                        label: t('menu.toSearch'),
                        path: '/clients/search',
                        activePaths: ['/clients/dashboard']
                    },
                    {
                        id: 'customers-new',
                        label: t('menu.new'),
                        path: '/clients/new'
                    },
                    {
                        id: 'customers-targetting',
                        label: t('menu.targetingExtractFiles'),
                        path: '/clients/targetting-extract-files'
                    },
                    {
                        id: 'customers-import',
                        label: t('menu.listImport'),
                        path: '/clients/list-import'
                    }
                ]
            },
            {
                id: 'store',
                label: t('menu.store'),
                icon: 'bx bx-store',
                subMenu: [
                    {
                        id: 'store-new',
                        label: t('menu.addNewStore'),
                        path: '/store/add-new-store'
                    },
                    {
                        id: 'store-show',
                        label: t('menu.showStore'),
                        path: '/store/show-store'
                    },
                    {
                        id: 'store-edit',
                        label: t('menu.updateStore'),
                        path: '/store/update-store'
                    }
                ]
            },
            {
                id: 'acoustic',
                label: t('menu.acoustic'),
                icon: 'bx bx-headphone',
                subMenu: [
                    {
                        id: 'acoustic-new-offer',
                        label: t('menu.newOfferRequest'),
                        path: '/acoustic/new'
                    },
                    {
                        id: 'acoustic-offers-list',
                        label: t('menu.offerRequestsList'),
                        path: '/acoustic/search'
                    },
                    {
                        id: 'acoustic-offer-details',
                        label: t('menu.offerRequestDetails'),
                        path: '/acoustic/offer-request-details'
                    }
                ]
            }
        ];

        // Tant que les droits ne sont pas chargés, on n'affiche rien plutôt que des entrées qui disparaîtraient ensuite
        return rightsReady ? filterByRights(menu, canOpen) : [];
    }, [t, reportingMenuItems, powerBiLoading, rightsReady, canOpen]);

    // Droits ou configuration d'enseigne en cours de chargement : le menu affiche un skeleton à la place des entrées
    const loading = (!rightsReady && !rightsFailed) || brandLoading;

    return { menuItems: menuStructure, loading };
};

/**
 * Helper function to convert RouteType from useGetDynamicReportingMenu to MenuItem format
 */
const convertRouteTypeToMenuItem = (routeItem: any): MenuItem => {
    const menuItem: MenuItem = {
        id: routeItem.name?.toLowerCase().replace(/\s+/g, '-') || 'powerbi',
        label: routeItem.name || 'Reporting DataViz',
        icon: typeof routeItem.icon === 'string' ? routeItem.icon : 'bx bx-bar-chart-alt-2',
        isLoading: routeItem.isLoading || false,
    };

    if (routeItem.isLoading) {
        const MenuLoadingSpinner = require('../components/Menu/MenuLoadingSpinner').default;
        menuItem.subMenu = [{ id: 'powerbi-loading', label: '', component: MenuLoadingSpinner }];
        return menuItem;
    }

    if (routeItem.children?.length > 0) {
        menuItem.subMenu = routeItem.children.map((block: any, blockIndex: number) => ({
            id: `powerbi-block-${blockIndex}`,
            label: block.name,
            icon: typeof block.icon === 'string' ? block.icon : getIconClassForBlock(block.name),
            subMenu: block.children?.map((report: any, reportIndex: number) => ({
                id: `report-${blockIndex}-${reportIndex}`,
                label: report.name,
                path: report.path
            })),
        }));
    } else {
        menuItem.subMenu = [];
    }

    return menuItem;
};

const getIconClassForBlock = (blockName: string): string => {
    const lower = blockName?.toLowerCase() || '';
    const iconMap: Record<string, string> = {
        'chiffres|key': 'bx bx-trending-up',
        'client|base': 'bx bx-group',
        'fidel|loyalty': 'bx bx-heart',
        'animation|operation': 'bx bx-envelope',
        'activit|activity': 'bx bx-bar-chart-square',
        'dashboard': 'bx bx-dashboard',
    };

    for (const [keys, icon] of Object.entries(iconMap)) {
        if (keys.split('|').some(key => lower.includes(key))) {
            return icon;
        }
    }
    
    return 'bx bx-bar-chart-alt-2';
};
